import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export interface Role {
  id: number;
  user_role: string;
}

export interface EmployeeData {
  id: number;
  username: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  contact: string | null;
  address: string | null;
  status: number;
  role_id: number | null;
  role: string | null;
  employee_id: number | null;
  emp_code: string | null;
}

interface AdminEmployeeState {
  roles: Role[];
  rolesLoading: boolean;
  employees: EmployeeData[];
  listLoading: boolean;
  selectedEmployee: EmployeeData | null;
  employeeLoading: boolean;
  submitting: boolean;
}

const initialState: AdminEmployeeState = {
  roles: [],
  rolesLoading: false,
  employees: [],
  listLoading: false,
  selectedEmployee: null,
  employeeLoading: false,
  submitting: false,
};

export const fetchEmployeeList = createAsyncThunk(
  "adminEmployee/fetchList",
  async (_, { rejectWithValue }) => {
    const res = await fetch("/api/admin/employees");
    const data = await res.json();
    if (!res.ok) return rejectWithValue("Failed to fetch employees");
    return data.employees as EmployeeData[];
  }
);

export const fetchRoles = createAsyncThunk(
  "adminEmployee/fetchRoles",
  async (_, { rejectWithValue }) => {
    const res = await fetch("/api/admin/roles");
    const data = await res.json();
    if (!res.ok) return rejectWithValue("Failed to fetch roles");
    return data.roles as Role[];
  }
);

export const fetchEmployeeById = createAsyncThunk(
  "adminEmployee/fetchById",
  async (id: string, { rejectWithValue }) => {
    const res = await fetch(`/api/admin/employees/${id}`);
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to load employee data.");
    return data.employee as EmployeeData;
  }
);

export interface CreateEmployeePayload {
  username: string;
  password: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  contact: string | null;
  address: string | null;
  roleId: number | null;
  status: number;
}

export const createEmployee = createAsyncThunk(
  "adminEmployee/create",
  async (payload: CreateEmployeePayload, { rejectWithValue }) => {
    const res = await fetch("/api/admin/employees", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to create employee.");
    return data.message as string;
  }
);

export interface UpdateEmployeePayload {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  contact: string | null;
  address: string | null;
  roleId: number | null;
  status: number;
  password?: string;
  empCode?: string;
}

export const updateEmployee = createAsyncThunk(
  "adminEmployee/update",
  async (payload: UpdateEmployeePayload, { rejectWithValue }) => {
    const res = await fetch("/api/admin/employees", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to update employee.");
    return data.message as string;
  }
);

const adminEmployeeSlice = createSlice({
  name: "adminEmployee",
  initialState,
  reducers: {
    clearSelectedEmployee(state) {
      state.selectedEmployee = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchEmployeeList.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchEmployeeList.fulfilled, (state, action) => {
        state.listLoading = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployeeList.rejected, (state) => {
        state.listLoading = false;
      })
      .addCase(fetchRoles.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.rolesLoading = false;
      })
      .addCase(fetchEmployeeById.pending, (state) => {
        state.employeeLoading = true;
        state.selectedEmployee = null;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.selectedEmployee = action.payload;
      })
      .addCase(fetchEmployeeById.rejected, (state) => {
        state.employeeLoading = false;
      })
      .addCase(createEmployee.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createEmployee.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(createEmployee.rejected, (state) => {
        state.submitting = false;
      })
      .addCase(updateEmployee.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateEmployee.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(updateEmployee.rejected, (state) => {
        state.submitting = false;
      });
  },
});

export const { clearSelectedEmployee } = adminEmployeeSlice.actions;
export default adminEmployeeSlice.reducer;
